import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import propTypes from '../../node_modules/prop-types/index.js';
import React, { useState } from 'react';
import classnames from '../../node_modules/classnames/index.js';
import { styled, useFormControl, FormGroup, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import useInputLookupData from './useInputLookupData.js';
import withLookup$1 from '../../cinnamon-core/es/components/lookups/withLookup.js';

var _excluded = ["value", "onChange", "options", "requestLookupWhenDisabled", "inline", "fullWidth", "verticalSpacing", "horizontalSpacing", "loading", "disabled", "readOnly", "groupRef", "lookup", "lookupAction", "lookupParams", "lookupOptions", "canSelect"],
    _excluded2 = ["disabled"];

var serializeValue = function serializeValue(val) {
  var _val$toString;

  return typeof val === "string" ? val : (_val$toString = val === null || val === void 0 ? void 0 : val.toString()) !== null && _val$toString !== void 0 ? _val$toString : "";
};

var CheckboxGroupBase = function CheckboxGroupBase(_ref) {
  var value = _ref.value,
      _onChange = _ref.onChange,
      options = _ref.options,
      requestLookupWhenDisabled = _ref.requestLookupWhenDisabled;
      _ref.inline;
      _ref.fullWidth;
      _ref.verticalSpacing;
      _ref.horizontalSpacing;
      var loading = _ref.loading,
      disabled = _ref.disabled,
      readOnly = _ref.readOnly,
      groupRef = _ref.groupRef,
      lookup = _ref.lookup;
      _ref.lookupAction;
      var lookupParams = _ref.lookupParams,
      lookupOptions = _ref.lookupOptions,
      canSelect = _ref.canSelect,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      uncontrolledValue = _useState2[0],
      setUncontrolledValue = _useState2[1];

  var canRequestData = !disabled || requestLookupWhenDisabled;
  var lookupData = useInputLookupData(lookup, lookupParams, lookupOptions, canRequestData);
  var checkboxGroupOptions = lookup ? Array.isArray(lookupData.response) ? lookupData.response : [] : options !== null && options !== void 0 ? options : [];
  var checkboxGroupValue = value === undefined ? uncontrolledValue : Array.isArray(value) ? value : [];
  var isLoading = lookup ? lookupData.loading || lookupData.loading === undefined : loading;
  return /*#__PURE__*/React.createElement(FormGroup, _extends({}, otherProps, {
    ref: groupRef
  }), isLoading ? /*#__PURE__*/React.createElement(CircularProgress, {
    color: "inherit",
    size: 30,
    className: classnames({
      hidden: lookup && !lookupData.slowLoading
    })
  }) : checkboxGroupOptions.map(function (o) {
    var isDisabled = disabled || (canSelect ? !canSelect(o) : false);
    var isChecked = checkboxGroupValue.indexOf(o.value) >= 0;
    var serializedValue = serializeValue(o.value);
    return /*#__PURE__*/React.createElement(FormControlLabel, {
      key: o.key || serializedValue,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: isChecked,
        value: serializedValue,
        onChange: function onChange(e, checked) {
          if (readOnly) return;
          var newValue = checked ? [].concat(_toConsumableArray(checkboxGroupValue), [o.value]) : checkboxGroupValue.filter(function (v) {
            return v !== o.value;
          });
          setUncontrolledValue(newValue);
          if (_onChange) _onChange(e, newValue, o.value, checked);
        },
        readOnly: readOnly
      }),
      label: o.label,
      disabled: isDisabled
    });
  }));
};

CheckboxGroupBase.propTypes = {
  value: propTypes.oneOfType([propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number, propTypes.bool])), propTypes.oneOf([null])]),
  onChange: propTypes.func,
  options: propTypes.any,
  requestLookupWhenDisabled: propTypes.bool,
  inline: propTypes.bool,
  fullWidth: propTypes.bool,
  verticalSpacing: propTypes.oneOfType([propTypes.number, propTypes.string]),
  horizontalSpacing: propTypes.oneOfType([propTypes.number, propTypes.string]),
  loading: propTypes.bool,
  disabled: propTypes.bool,
  canSelect: propTypes.func,
  readOnly: propTypes.bool,
  groupRef: propTypes.oneOfType([propTypes.string, propTypes.func, propTypes.object]),
  lookup: propTypes.any,
  lookupAction: propTypes.func,
  lookupParams: propTypes.any,
  lookupOptions: propTypes.any
};
var StyledCheckboxGroup = styled(CheckboxGroupBase)(function (_ref2) {
  var inline = _ref2.inline,
      fullWidth = _ref2.fullWidth,
      verticalSpacing = _ref2.verticalSpacing,
      horizontalSpacing = _ref2.horizontalSpacing,
      theme = _ref2.theme;
  var vMargin = typeof verticalSpacing === "number" ? theme.spacing(verticalSpacing) : verticalSpacing;
  var hMargin = typeof horizontalSpacing === "number" ? theme.spacing(horizontalSpacing) : horizontalSpacing;
  return {
    flexDirection: inline ? "row" : undefined,
    flexWrap: inline ? "wrap" : undefined,
    alignItems: inline ? undefined : "flex-start",
    "> .MuiCircularProgress-root": {
      margin: theme.spacing(0.5),
      "&.hidden": {
        opacity: 0
      }
    },
    ".MuiFormControlLabel-root": {
      marginBottom: vMargin,
      marginRight: inline ? hMargin : 0,
      width: !inline && fullWidth ? "100%" : undefined,
      "&:last-child": {
        marginBottom: inline ? undefined : 0,
        marginRight: inline ? 0 : undefined
      }
    },
    ".MuiCheckbox-root": {
      padding: "0.2em",
      marginLeft: "0.4em",
      marginRight: "0.4em"
    }
  };
});
var LookupCheckboxGroup = withLookup$1({
  autoNamePrefix: "LookupCheckboxGroup/",
  required: false
})(StyledCheckboxGroup);
/**
 * A group of selectable checkboxes with optional lookup data connectivity
 */

var CheckboxGroup = function CheckboxGroup(_ref3) {
  var disabled = _ref3.disabled,
      props = _objectWithoutProperties(_ref3, _excluded2);

  var formControl = useFormControl();
  return /*#__PURE__*/React.createElement(LookupCheckboxGroup, _extends({}, props, {
    disabled: disabled !== null && disabled !== void 0 ? disabled : formControl === null || formControl === void 0 ? void 0 : formControl.disabled
  }));
};

CheckboxGroup.propTypes = {
  value: propTypes.oneOfType([propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number, propTypes.bool])), propTypes.oneOf([null])]),
  onChange: propTypes.func,
  options: propTypes.any,
  requestLookupWhenDisabled: propTypes.bool,
  inline: propTypes.bool,
  fullWidth: propTypes.bool,
  verticalSpacing: propTypes.oneOfType([propTypes.number, propTypes.string]),
  horizontalSpacing: propTypes.oneOfType([propTypes.number, propTypes.string]),
  loading: propTypes.bool,
  disabled: propTypes.bool,
  canSelect: propTypes.func,
  readOnly: propTypes.bool,
  groupRef: propTypes.oneOfType([propTypes.string, propTypes.func, propTypes.object]),
  lookup: propTypes.any,
  lookupAction: propTypes.func,
  lookupParams: propTypes.any,
  lookupOptions: propTypes.any
};
CheckboxGroup.defaultProps = {
  requestLookupWhenDisabled: false,
  verticalSpacing: 0.5,
  horizontalSpacing: 4
};
var ICheckboxGroup = CheckboxGroup;

export { ICheckboxGroup as default };
